import {
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface Props {
  open: boolean;
  /** the row clicked in the table - every field is already on it */
  calculationType: any;
  onClose: () => void;
}

// a status the record either has or does not have, spelled out either way so
// the reader never has to guess what a missing chip means
const StatusChip = ({
  active,
  onLabel,
  offLabel,
  color,
  icon,
  offColor = "text.disabled",
  offIcon,
}: {
  active: boolean;
  onLabel: string;
  offLabel: string;
  color: string;
  icon: React.ReactNode;
  offColor?: string;
  offIcon?: React.ReactNode;
}) => {
  const shownColor = active ? color : offColor;

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Box sx={{ display: "flex", color: shownColor }} component="span">
        {active ? icon : (offIcon ?? icon)}
      </Box>
      <Typography
        variant="body2"
        sx={{ color: shownColor, whiteSpace: "nowrap" }}
      >
        {active ? onLabel : offLabel}
      </Typography>
    </Stack>
  );
};

const DetailField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", display: "block" }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Paper sx={{ padding: "16px", mb: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

const CalculationTypeDetailsDrawer = ({
  open,
  calculationType,
  onClose,
}: Props) => {
  const isActive = Boolean(calculationType?.is_active);
  const isDocumented = Boolean(calculationType?.deep_view_is_documented);
  const hasMissedRunWarning = Boolean(calculationType?.has_missed_run_warning);
  const modified = calculationType?.modified;

  return (
    <Drawer
      anchor="right"
      sx={{ zIndex: 1300 }}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "40%", minWidth: "420px" } }}
    >
      {calculationType && (
        <Box
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          data-testid="calculation-type-details-drawer"
        >
          <Box sx={{ padding: "16px 16px 12px" }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={2}
            >
              <Typography variant="h3" sx={{ fontSize: "20px" }}>
                {calculationType.name || "—"}
              </Typography>
              <IconButton onClick={onClose} size="small" aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Calculation ID {calculationType.id}
              </Typography>
              <StatusChip
                active={isActive}
                onLabel="Active"
                offLabel="Inactive"
                color="success.main"
                icon={<CheckIcon fontSize="small" />}
                offColor="error.main"
                offIcon={<CloseIcon fontSize="small" />}
              />
              <StatusChip
                active={isDocumented}
                onLabel="Documented"
                offLabel="Not Documented"
                color="success.main"
                icon={<CheckIcon fontSize="small" />}
                offColor="error.main"
                offIcon={<CloseIcon fontSize="small" />}
              />
              <StatusChip
                active={hasMissedRunWarning}
                onLabel="Missed Run Warning"
                offLabel="No Missed Run Warning"
                color="warning.main"
                icon={<WarningAmberIcon fontSize="small" />}
              />
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ padding: "16px", overflowY: "auto", flex: 1 }}>
            <Section title="Details">
              <DetailField
                label="Description"
                value={calculationType.description}
              />
              <DetailField
                label="Atom Phrase — Stage 2 Explanation"
                value={calculationType.atom_phrase}
              />
              <DetailField
                label="Custom Expression"
                value={calculationType.custom_expression}
              />
            </Section>

            <Section title="Deep View Documentation">
              <DetailField
                label="Deep View Explanation"
                value={calculationType.deep_view_explanation}
              />
              <DetailField
                label="Plain English Text"
                value={calculationType.plain_english_text}
              />
            </Section>

            <Section title="Audit">
              <Grid container spacing={2}>
                <Grid size={6}>
                  <DetailField
                    label="Created"
                    value={calculationType.created}
                  />
                </Grid>
                <Grid size={6}>
                  <DetailField label="By" value={calculationType.created_by} />
                </Grid>
                <Grid size={6}>
                  <DetailField label="Modified" value={modified || "Never"} />
                </Grid>
                <Grid size={6}>
                  <DetailField label="By" value={calculationType.modified_by} />
                </Grid>
              </Grid>
            </Section>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default CalculationTypeDetailsDrawer;
